/**
 * Player API — Catalog.
 *
 * Read-only endpoints that power the player-facing storefront in the player's
 * browser: browsing the games (packs) you offer, viewing a single game's odds,
 * listing the games where the player still holds sealed packs, the category
 * pills, and the "potential hits" card grid for a game.
 *
 * Every operation here is called from the player's browser (your custom UI) and
 * is authenticated with the player session token, supplied through the
 * `PlatformSession` security scheme. A missing, invalid, or expired token
 * returns `401`. Money is returned in the currency you configured: decimal
 * prices are emitted as strings (for example `"19.99"`), card values and odds
 * as numbers.
 */
import { oddsLevelSchema } from './components';
import {
  CUSTOM_PACK_UNAVAILABLE_REASONS,
  CUSTOM_PACK_UNPRICED_NOTE,
  customPackErrorSchema,
  customPackInvalidSelectionExample,
  customPackQuoteExample,
  customPackQuoteSchema,
  customPackSelectionItems,
} from './components.customPack';
import {
  customPackCardsExamples,
  customPackCardsSchema,
  customPackSearchParameters,
  retryAfterHeaders,
} from './components.customPackCatalog';

export const catalogPaths: Record<string, unknown> = {
  '/loot_machine/api/game_platform/iframe/v1/items': {
    get: {
      tags: ['Catalog'],
      summary: 'List most expensive cards',
      description: [
        'Returns the most valuable cards the player can currently pull across the games (packs)',
        'available on this session — use it to render a "top prizes" showcase in your UI.',
        '',
        '`sort` selects the ordering (only `most_expensive` today); `game_id`, `min_value` and',
        '`limit` narrow the result. Card `value` is a number in USD, and each card carries the',
        'pack it belongs to (`game_id` / `game_name`).',
      ].join('\n'),
      operationId: 'listMostExpensiveItems',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'sort',
          in: 'query',
          required: false,
          schema: { type: 'string', enum: ['most_expensive'], default: 'most_expensive' },
          description: 'Ordering of the cards. Currently only `most_expensive` (highest value first).',
        },
        {
          name: 'game_id',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description: 'Limit to a single pack available on this session.',
        },
        {
          name: 'min_value',
          in: 'query',
          required: false,
          schema: { type: 'number' },
          description: 'Only return cards worth at least this much (USD).',
        },
        {
          name: 'limit',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 20, maximum: 50 },
          description: 'How many cards to return. Defaults to 20, capped at 50.',
        },
      ],
      responses: {
        '200': {
          description: 'Cards ordered by the chosen sort.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'PlayerItemListResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['items'],
                    properties: {
                      items: {
                        type: 'array',
                        items: {
                          type: 'object',
                          title: 'PlayerItem',
                          properties: {
                            id: { type: 'integer', description: 'Unique id of the card.' },
                            title: { type: 'string', description: 'Card title.' },
                            image_url: {
                              type: 'string',
                              format: 'uri',
                              nullable: true,
                              description: 'Card image URL; may be null.',
                            },
                            value: { type: 'number', nullable: true, description: 'Card value in USD.' },
                            game_id: { type: 'integer', description: 'Id of the pack this card belongs to.' },
                            game_name: {
                              type: 'string',
                              nullable: true,
                              description:
                                'Name of the pack this card belongs to — reflects the pack rename set in the console, when one is configured.',
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  items: [
                    {
                      id: 90123,
                      title: 'Conor McGregor — Championship Auto',
                      image_url: 'https://cdn.getcardbase.com/blobs/card_90123.png',
                      value: 500,
                      game_id: 463,
                      game_name: 'HOF ELITE MMA Pack',
                    },
                  ],
                },
              },
            },
          },
        },
        '401': {
          description: 'Missing, invalid, or expired session token — `INVALID_TOKEN`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_offers': {
    get: {
      tags: ['Catalog'],
      summary: 'List available games',
      description: [
        'Returns the paginated catalog of games (packs) available to the player on this',
        'session. This is the primary endpoint for rendering your storefront grid and the',
        'related-games slider shown when a player drills into a category.',
        '',
        'Use `category_id` together with `category_class_name` to scope the list to a single',
        'category. Use `order_by` and `order_direction` to control sorting, and `page` / `per`',
        'to page through results. Prices are returned as strings in the player wallet currency.',
        '',
        'Custom pack games (`game_kind: custom_pack`) are not listed here. A custom pack is opened by its id, which',
        'the server-side `GET /v1/games` returns with `game_type: "custom_pack"`.',
      ].join('\n'),
      operationId: 'listGames',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'category_id',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description:
            'Restrict the list to games in a single category. Use together with `category_class_name`.',
        },
        {
          name: 'category_class_name',
          in: 'query',
          required: false,
          schema: { type: 'string' },
          description:
            'An opaque category-type identifier obtained from the categories endpoint. Pass back verbatim the `category_type` value the categories endpoint returned, alongside `category_id`, to scope the games list to that category. An unrecognized value applies no filter.',
        },
        {
          name: 'is_multiple',
          in: 'query',
          required: false,
          schema: { type: 'boolean' },
          description: 'When set, returns only games that sell multiple packs at once.',
        },
        {
          name: 'order_by',
          in: 'query',
          required: false,
          schema: { type: 'string', enum: ['price', 'sales_count'], default: 'price' },
          description: 'Sort field. Accepts `price` or `sales_count`; defaults to `price`.',
        },
        {
          name: 'order_direction',
          in: 'query',
          required: false,
          schema: { type: 'string', enum: ['asc', 'desc'], default: 'asc' },
          description: 'Sort direction. Accepts `asc` or `desc`; defaults to `asc`.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 1, minimum: 1 },
          description: '1-based page number. Defaults to 1.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 32, maximum: 100 },
          description: 'Number of games per page. Defaults to 32, capped at 100.',
        },
        { $ref: '#/components/parameters/WithOddsLevels' },
      ],
      responses: {
        '200': {
          description: 'A page of games.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'GameList',
                required: ['pool_offers', 'meta'],
                properties: {
                  pool_offers: {
                    type: 'array',
                    description: 'The games on this page.',
                    items: {
                      type: 'object',
                      title: 'GameCard',
                      required: ['id', 'title', 'status', 'is_multiple', 'suitable_badges'],
                      properties: {
                        id: { type: 'integer', description: 'Unique game identifier.' },
                        title: {
                          type: 'string',
                          description:
                            'Display name of the game. The name shown to players — reflects the pack rename set in the console, when one is configured.',
                        },
                        price: {
                          type: 'string',
                          nullable: true,
                          description: 'Price per pack as a decimal string, e.g. "19.99". May be null.',
                        },
                        status: {
                          type: 'string',
                          description: 'Availability of the game; available games are returned as "available".',
                        },
                        is_multiple: {
                          type: 'boolean',
                          description: 'Whether the game sells multiple packs at once.',
                        },
                        badge: {
                          type: 'object',
                          nullable: true,
                          title: 'GameOwnBadge',
                          description: "This game's own tier badge. Null when the game has no tier.",
                          required: ['name'],
                          properties: {
                            name: { type: 'string', description: 'Badge label.' },
                          },
                        },
                        pack_image_url: {
                          type: 'string',
                          nullable: true,
                          format: 'uri',
                          description: 'Pack artwork image. Null when no image is set.',
                        },
                        pack_logo_image_url: {
                          type: 'string',
                          nullable: true,
                          format: 'uri',
                          description: 'Optional pack logo image you can override per platform. Null when not set.',
                        },
                        pedestal_image_url: {
                          type: 'string',
                          nullable: true,
                          format: 'uri',
                          description: 'Optional pedestal image you can override per platform. Null when not set.',
                        },
                        suitable_badges: {
                          type: 'array',
                          description:
                            'Tier badges used to position this game against its related games in a category. Empty when there are none.',
                          items: {
                            type: 'object',
                            title: 'GameBadge',
                            required: ['id', 'name', 'pool_offer_id', 'is_self'],
                            properties: {
                              id: { type: 'integer', description: 'Badge identifier.' },
                              name: { type: 'string', description: 'Badge label.' },
                              color: {
                                type: 'string',
                                nullable: true,
                                description: 'Badge color. May be null.',
                              },
                              pool_offer_id: {
                                type: 'integer',
                                description: 'Identifier of the game this badge belongs to.',
                              },
                              is_self: {
                                type: 'boolean',
                                description: 'True when the badge represents the current game itself.',
                              },
                              price: {
                                type: 'string',
                                nullable: true,
                                description: 'Price of the related game as a decimal string. May be null.',
                              },
                              image: {
                                allOf: [{ $ref: '#/components/schemas/ImageObject' }],
                                nullable: true,
                                description: "Pack image of the related game. Null when not set.",
                              },
                            },
                          },
                        },
                        min_value: {
                          type: 'number',
                          description: 'Lowest possible card value in USD. 0 when no odds are configured.',
                        },
                        max_value: {
                          type: 'number',
                          description: 'Highest tier floor value in USD. 0 when no odds are configured.',
                        },
                        odds: {
                          type: 'array',
                          description: 'Per-tier odds, sorted from the lowest value tier upward. Empty when no odds are configured.',
                          items: {
                            type: 'object',
                            title: 'OddsTier',
                            required: ['tier_name', 'range', 'percentage'],
                            properties: {
                              tier_name: {
                                type: 'string',
                                description: 'Tier label, e.g. "Common", "Uncommon", "Legendary".',
                              },
                              range: {
                                type: 'string',
                                description: 'Formatted value range for the tier, e.g. "$0 - $50" or "$500+".',
                              },
                              percentage: {
                                type: 'number',
                                description: 'Probability of pulling a card in this tier, as a percentage.',
                              },
                            },
                          },
                        },
                        big_win_chance: {
                          type: 'number',
                          description: 'Headline chance, as a percentage, of pulling a card above the lowest tier. 0 when no odds are configured.',
                        },
                        operator_currency: {
                          type: 'string',
                          nullable: true,
                          description: 'Currency code of the player wallet, e.g. "EUR". Null when not configured.',
                        },
                        exchange_rate: {
                          type: 'number',
                          format: 'float',
                          nullable: true,
                          description: 'Conversion rate from your currency to USD. Null when not configured.',
                        },
                        estimated_value: {
                          type: 'number',
                          description: 'Estimated value of the game in USD; falls back to the pack price when not set.',
                        },
                        max_for_purchase_count: {
                          type: 'integer',
                          description: 'Maximum number of packs a player may buy in one order for this game. 0 when out of stock.',
                        },
                        odds_levels: {
                          type: 'object',
                          title: 'OddsLevels',
                          description:
                            'Per-volatility-level odds (Normal / High / Max) for this game. Only present when the request is sent with with_odds_levels=true. Same shape as the game-detail `odds_levels`; each level mirrors the top-level odds + value stats.',
                          required: ['normal', 'high', 'max'],
                          properties: {
                            normal: oddsLevelSchema,
                            high: oddsLevelSchema,
                            max: oddsLevelSchema,
                          },
                        },
                      },
                    },
                  },
                  meta: { $ref: '#/components/schemas/PaginationMeta' },
                },
              },
              example: {
                pool_offers: [
                  {
                    id: 463,
                    title: 'HOF ELITE MMA Pack',
                    price: '99.99',
                    status: 'available',
                    is_multiple: false,
                    badge: { name: 'Legendary' },
                    pack_image_url: 'https://cdn.getcardbase.com/blobs/pack_463.png',
                    pack_logo_image_url: 'https://cdn.getcardbase.com/blobs/logo_463.png',
                    pedestal_image_url: null,
                    suitable_badges: [
                      {
                        id: 7,
                        color: '#FFD700',
                        name: 'Legendary',
                        pool_offer_id: 463,
                        is_self: true,
                        price: '99.99',
                        image: {
                          url: 'https://cdn.getcardbase.com/blobs/pack_463.png',
                          metadata: { width: 600, height: 800 },
                          signed_id: 'eyJfcmFpbHMiOnsi...',
                        },
                      },
                    ],
                    min_value: 5,
                    max_value: 500,
                    odds: [
                      { tier_name: 'Common', range: '$5 - $50', percentage: 78.5 },
                      { tier_name: 'Uncommon', range: '$50 - $200', percentage: 18 },
                      { tier_name: 'Legendary', range: '$500+', percentage: 3.5 },
                    ],
                    big_win_chance: 21.5,
                    operator_currency: 'EUR',
                    exchange_rate: 1.08,
                    estimated_value: 120,
                    max_for_purchase_count: 3,
                  },
                ],
                meta: { total_count: 12, page: 1, per: 32 },
              },
            },
          },
        },
        '401': {
          description: 'The player session token is missing, invalid, or expired.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected server error. The response carries a tracking id you can quote to support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_offers/{id}': {
    get: {
      tags: ['Catalog'],
      summary: 'Get a game',
      description: [
        'Returns a single game with its full odds breakdown, stock, pricing, and media assets.',
        'Use this to render the game screen before a player buys a pack: the per-tier `odds`',
        'array, the headline `big_win_chance`, the `estimated_value`, and any pack videos.',
        '',
        '`game_kind` tells the two kinds of game apart. A `loot_pack` is a regular pack with fixed odds. A',
        '`custom_pack` (Custom Pack Lab) has none: the player picks the cards and sets their chances, and pays the',
        'expected value of that selection divided by the game’s RTP. Instead of `odds`, `odds_levels`,',
        '`big_win_chance`, `min_value`, `max_value`, `estimated_value` and `max_for_purchase_count` — which a custom',
        'pack does not have — it carries a `custom_pack` object with the RTP and the limits to build within, and',
        'its `price` is not what the player pays. A custom pack is bought in three calls: **Search cards for a',
        'custom pack**, **Get a custom pack quote** for the selection, then **Buy a pack (one call)** — the only',
        'way to buy one.',
        '',
        'If the game does not exist or is not available to the player (a custom pack is not, for instance, on a',
        'platform that requires provably fair), this returns `404`.',
      ].join('\n'),
      operationId: 'getGame',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Identifier of the game to retrieve.',
        },
      ],
      responses: {
        '200': {
          description: 'The requested game.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'GameDetailResponse',
                required: ['pool_offer'],
                properties: {
                  pool_offer: {
                    type: 'object',
                    title: 'GameDetail',
                    required: ['id', 'title', 'status', 'game_kind'],
                    properties: {
                      id: { type: 'integer', description: 'Unique game identifier.' },
                      title: {
                        type: 'string',
                        description:
                          'Display name of the game. The name shown to players — reflects the pack rename set in the console, when one is configured.',
                      },
                      game_kind: {
                        type: 'string',
                        enum: ['loot_pack', 'custom_pack'],
                        description:
                          '`loot_pack` for a regular pack with fixed odds; `custom_pack` for a Custom Pack Lab game, where ' +
                          'the player builds the pack. A `custom_pack` game carries `custom_pack` instead of the odds, ' +
                          'stock and estimated-value fields.',
                      },
                      custom_pack: {
                        type: 'object',
                        title: 'CustomPackRules',
                        description:
                          'Present only when `game_kind` is `custom_pack`: the RTP the price is based on and the limits a ' +
                          'selection must stay within.',
                        required: ['rtp_ppm', 'limits'],
                        properties: {
                          rtp_ppm: {
                            type: 'integer',
                            description: 'The RTP this game is priced at, in parts per million (900000 is 90%).',
                            example: 900000,
                          },
                          limits: {
                            type: 'object',
                            title: 'CustomPackLimits',
                            required: ['min_cards', 'max_cards', 'min_chance_permille', 'max_chance_permille'],
                            properties: {
                              min_cards: { type: 'integer', description: 'Fewest cards in a selection.', example: 5 },
                              max_cards: { type: 'integer', description: 'Most cards in a selection.', example: 10 },
                              min_chance_permille: {
                                type: 'integer',
                                description: 'Lowest chance one card may have, in permille (1 is 0.1%).',
                                example: 1,
                              },
                              max_chance_permille: {
                                type: 'integer',
                                description: 'Highest chance one card may have, in permille (500 is 50%).',
                                example: 500,
                              },
                            },
                          },
                        },
                      },
                      price: {
                        type: 'string',
                        nullable: true,
                        description:
                          'Price per pack as a decimal string, e.g. "99.99". May be null. Not what a player pays for a ' +
                          '`custom_pack` game, which has no fixed price — price a selection with **Get a custom pack quote**.',
                      },
                      pack_image_url: {
                        type: 'string',
                        nullable: true,
                        format: 'uri',
                        description: 'Pack artwork image. Null when no image is set.',
                      },
                      bg_image_url: {
                        type: 'string',
                        nullable: true,
                        format: 'uri',
                        description: 'Optional background image you can override per platform. Null when not set.',
                      },
                      pack_logo_image_url: {
                        type: 'string',
                        nullable: true,
                        format: 'uri',
                        description: 'Optional pack logo image you can override per platform. Null when not set.',
                      },
                      pedestal_image_url: {
                        type: 'string',
                        nullable: true,
                        format: 'uri',
                        description: 'Optional pedestal image you can override per platform. Null when not set.',
                      },
                      status: {
                        type: 'string',
                        description: 'Availability of the game (e.g. "available", "restocking").',
                      },
                      max_for_purchase_count: {
                        type: 'integer',
                        description:
                          'Maximum number of packs a player may buy in one order for this game. 0 when out of stock. Absent for a `custom_pack` game.',
                      },
                      estimated_value: {
                        type: 'number',
                        description:
                          'Estimated value of the game in USD; falls back to the pack price when not set. Absent for a `custom_pack` game.',
                      },
                      min_value: {
                        type: 'number',
                        description:
                          'Lowest possible card value in USD. 0 when no odds are configured. Absent for a `custom_pack` game.',
                      },
                      odds: {
                        type: 'array',
                        description:
                          'Per-tier odds, sorted from the lowest value tier upward. Empty when no odds are configured. Absent for a `custom_pack` game.',
                        items: {
                          type: 'object',
                          title: 'OddsTier',
                          required: ['tier_name', 'range', 'percentage'],
                          properties: {
                            tier_name: {
                              type: 'string',
                              description: 'Tier label, e.g. "Common", "Uncommon", "Legendary".',
                            },
                            range: {
                              type: 'string',
                              description: 'Formatted value range for the tier, e.g. "$0 - $50" or "$500+".',
                            },
                            percentage: {
                              type: 'number',
                              description: 'Probability of pulling a card in this tier, as a percentage.',
                            },
                          },
                        },
                      },
                      big_win_chance: {
                        type: 'number',
                        description:
                          'Headline chance, as a percentage, of pulling a card above the lowest tier. 0 when no odds are configured. Absent for a `custom_pack` game.',
                      },
                      odds_levels: {
                        type: 'object',
                        title: 'OddsLevels',
                        description:
                          'Per-volatility-level odds for the Normal / High / Max selector shown to the player. Expected value is the same across levels; higher levels shift probability toward the extremes (more Common and a fatter top tier, a thinner middle). Each level mirrors the top-level `odds` + value stats. Absent for a `custom_pack` game.',
                        required: ['normal', 'high', 'max'],
                        properties: {
                          normal: oddsLevelSchema,
                          high: oddsLevelSchema,
                          max: oddsLevelSchema,
                        },
                      },
                      pack_video: {
                        type: 'string',
                        nullable: true,
                        format: 'uri',
                        description: 'Pack teaser video. Null when not set.',
                      },
                      open_pack_video: {
                        type: 'string',
                        nullable: true,
                        format: 'uri',
                        description: 'Pack reveal video. Null when not set.',
                      },
                      web_pack_video: {
                        type: 'string',
                        nullable: true,
                        format: 'uri',
                        description: 'Web-optimized pack teaser video. Null when not set.',
                      },
                      web_open_pack_video: {
                        type: 'string',
                        nullable: true,
                        format: 'uri',
                        description: 'Web-optimized pack reveal video. Null when not set.',
                      },
                      operator_currency: {
                        type: 'string',
                        nullable: true,
                        description: 'Currency code of the player wallet. Null when not configured.',
                      },
                      exchange_rate: {
                        type: 'number',
                        format: 'float',
                        nullable: true,
                        description: 'Conversion rate from your currency to USD. Null when not configured.',
                      },
                    },
                  },
                },
              },
              example: {
                pool_offer: {
                  id: 463,
                  title: 'HOF ELITE MMA Pack',
                  game_kind: 'loot_pack',
                  price: '99.99',
                  pack_image_url: 'https://cdn.getcardbase.com/blobs/pack_463.png',
                  bg_image_url: null,
                  pack_logo_image_url: 'https://cdn.getcardbase.com/blobs/logo_463.png',
                  pedestal_image_url: null,
                  status: 'available',
                  max_for_purchase_count: 3,
                  estimated_value: 120,
                  min_value: 5,
                  odds: [
                    { tier_name: 'Common', range: '$5 - $50', percentage: 78.5 },
                    { tier_name: 'Uncommon', range: '$50 - $200', percentage: 18 },
                    { tier_name: 'Legendary', range: '$500+', percentage: 3.5 },
                  ],
                  big_win_chance: 21.5,
                  odds_levels: {
                    normal: {
                      tiers: [
                        { tier_name: 'Common', range: '$5 - $50', percentage: 78.5 },
                        { tier_name: 'Uncommon', range: '$50 - $200', percentage: 18 },
                        { tier_name: 'Legendary', range: '$500+', percentage: 3.5 },
                      ],
                      min_value: 5,
                      max_value: 500,
                      big_win_chance: 21.5,
                    },
                    high: {
                      tiers: [
                        { tier_name: 'Common', range: '$5 - $50', percentage: 85 },
                        { tier_name: 'Uncommon', range: '$50 - $200', percentage: 8.5 },
                        { tier_name: 'Legendary', range: '$500+', percentage: 6.5 },
                      ],
                      min_value: 5,
                      max_value: 500,
                      big_win_chance: 15,
                    },
                    max: {
                      tiers: [
                        { tier_name: 'Common', range: '$5 - $50', percentage: 90 },
                        { tier_name: 'Uncommon', range: '$50 - $200', percentage: 0 },
                        { tier_name: 'Legendary', range: '$500+', percentage: 10 },
                      ],
                      min_value: 5,
                      max_value: 500,
                      big_win_chance: 10,
                    },
                  },
                  pack_video: 'https://cdn.getcardbase.com/blobs/pack_463.mp4',
                  open_pack_video: 'https://cdn.getcardbase.com/blobs/open_463.mp4',
                  web_pack_video: null,
                  web_open_pack_video: null,
                  operator_currency: 'EUR',
                  exchange_rate: 1.08,
                },
              },
            },
          },
        },
        '404': {
          description: `No game with this id is available to the player. ${CUSTOM_PACK_UNPRICED_NOTE}`,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '401': {
          description: 'The player session token is missing, invalid, or expired.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected server error. The response carries a tracking id you can quote to support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_offers/unopened': {
    get: {
      tags: ['Catalog'],
      summary: "List the player's sealed games",
      description: [
        'Returns one row per game in which the player still holds at least one unopened (sealed)',
        'pack, ordered by game id. Use this to build the "open your packs" view and let the player',
        'jump straight to revealing a pack they already own.',
        '',
        'This list is not paginated and returns no pagination metadata. Each row includes the',
        'count of sealed packs and the id of the most recent purchase round that contains one,',
        'so you can deep-link to the reveal flow.',
      ].join('\n'),
      operationId: 'listSealedGames',
      security: [{ PlatformSession: [] }],
      responses: {
        '200': {
          description: 'Games in which the player holds sealed packs.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'SealedGameList',
                required: ['pool_offers'],
                properties: {
                  pool_offers: {
                    type: 'array',
                    description: 'One row per game with at least one sealed pack, ordered by id. Not paginated.',
                    items: {
                      type: 'object',
                      title: 'SealedGame',
                      required: ['id', 'title', 'unopened_count'],
                      properties: {
                        id: { type: 'integer', description: 'Unique game identifier.' },
                        title: {
                          type: 'string',
                          description:
                            'Display name of the game. The name shown to players — reflects the pack rename set in the console, when one is configured.',
                        },
                        price: {
                          type: 'string',
                          nullable: true,
                          description: 'Price per pack as a decimal string, e.g. "19.99". May be null.',
                        },
                        pack_image_url: {
                          type: 'string',
                          nullable: true,
                          format: 'uri',
                          description: "The game's default pack artwork. Null when no image is set.",
                        },
                        unopened_count: {
                          type: 'integer',
                          description: 'Number of sealed packs the player owns for this game.',
                        },
                        first_unopened_order_id: {
                          type: 'integer',
                          nullable: true,
                          description:
                            'Id of the most recent purchase round that still contains a sealed pack, used to open the next pack. Null when none.',
                        },
                      },
                    },
                  },
                },
              },
              example: {
                pool_offers: [
                  {
                    id: 430,
                    title: 'HOF MMA Starter Pack',
                    price: '19.99',
                    pack_image_url: 'https://cdn.getcardbase.com/blobs/pack_430.png',
                    unopened_count: 3,
                    first_unopened_order_id: 90215,
                  },
                  {
                    id: 463,
                    title: 'HOF ELITE MMA Pack',
                    price: '99.99',
                    pack_image_url: 'https://cdn.getcardbase.com/blobs/pack_463.png',
                    unopened_count: 1,
                    first_unopened_order_id: 90250,
                  },
                ],
              },
            },
          },
        },
        '401': {
          description: 'The player session token is missing, invalid, or expired.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected server error. The response carries a tracking id you can quote to support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_categories': {
    get: {
      tags: ['Catalog'],
      summary: 'List game categories',
      description: [
        'Returns the categories that have at least one game available to the player, in display',
        'order. Use this to render the category pills above your storefront.',
        '',
        'Display order is the order you set under **Categories** in the console, lowest first.',
        'Categories without an order follow all ordered ones, in the default order.',
        '',
        'For each category, `id` is the identifier of a representative game (the cheapest available',
        'game in the category) that you can open directly when the pill is tapped, while',
        '`category_id` is the category identifier you pass to the games list to load the rest of',
        'the games in that category. This list is not paginated and returns no pagination metadata.',
      ].join('\n'),
      operationId: 'listGameCategories',
      security: [{ PlatformSession: [] }],
      responses: {
        '200': {
          description: 'Available game categories.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'CategoryList',
                required: ['categories'],
                properties: {
                  categories: {
                    type: 'array',
                    description: 'One entry per category with at least one available game.',
                    items: {
                      type: 'object',
                      title: 'GameCategory',
                      required: ['category_id', 'category_name', 'category_type'],
                      properties: {
                        id: {
                          type: 'integer',
                          nullable: true,
                          description:
                            'Identifier of a representative game in this category (the cheapest available game), used to open the category directly. May be null.',
                        },
                        category_id: {
                          type: 'integer',
                          description: 'Category identifier; pass this to the games list to load games in the category.',
                        },
                        category_name: { type: 'string', description: 'Display name of the category.' },
                        category_type: {
                          type: 'string',
                          description: 'An opaque category-type identifier; pass it back as `category_class_name` to filter the catalog.',
                        },
                        image: {
                          type: 'string',
                          nullable: true,
                          format: 'uri',
                          description: 'Category image. Null when no image is set.',
                        },
                      },
                    },
                  },
                },
              },
              example: {
                categories: [
                  {
                    id: 430,
                    category_id: 958,
                    category_name: 'MMA',
                    category_type: 'pack_category',
                    image: 'https://cdn.getcardbase.com/blobs/cat_mma.png',
                  },
                  {
                    id: 512,
                    category_id: 961,
                    category_name: 'Star Wars',
                    category_type: 'pack_category',
                    image: null,
                  },
                ],
              },
            },
          },
        },
        '401': {
          description: 'The player session token is missing, invalid, or expired.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected server error. The response carries a tracking id you can quote to support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_offers/{pool_offer_id}/pool_items': {
    get: {
      tags: ['Catalog'],
      summary: 'Cards you can pull from a game',
      description: [
        'Returns the paginated grid of "potential hits" — the cards a player can pull from a game,',
        'sorted from highest value down. Use this to show players the headline cards available in a',
        'pack before they buy.',
        '',
        'Each card includes its value, a tier badge, and, when a buyback offer is open, the price the',
        'player would receive for selling the card back. If the game does not exist or is not',
        'available to the player, this returns `404`.',
        '',
        'The grid is a showcase window: at most the top 50 cards are exposed, and `meta.total_count`',
        'never exceeds 50. Pages past that window return an empty list.',
      ].join('\n'),
      operationId: 'listGameCards',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Identifier of the game whose cards you want to list.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 1, minimum: 1 },
          description: '1-based page number. Defaults to 1.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 32, maximum: 100 },
          description: 'Number of cards per page. Defaults to 32, capped at 100; the grid never exposes more than the top 50 cards in total.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      responses: {
        '200': {
          description: 'A page of cards available in the game.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'GameCardGrid',
                required: ['pool_items', 'meta'],
                properties: {
                  pool_items: {
                    type: 'array',
                    description: 'The cards on this page, ordered from highest value down.',
                    items: {
                      type: 'object',
                      title: 'Card',
                      required: ['id', 'title', 'item_price'],
                      properties: {
                        id: { type: 'integer', description: 'Unique card identifier.' },
                        title: { type: 'string', description: 'Display name of the card. Empty string when unnamed.' },
                        image_url: {
                          type: 'string',
                          nullable: true,
                          format: 'uri',
                          description: 'Card image. Null when no image is available.',
                        },
                        item_price: {
                          type: 'number',
                          format: 'float',
                          description: 'Current market value of the card in USD.',
                        },
                        opened_at: {
                          type: 'string',
                          format: 'date-time',
                          nullable: true,
                          description: 'When the pack containing this card was revealed. Null until revealed.',
                        },
                        fair_market_value: {
                          type: 'number',
                          format: 'float',
                          nullable: true,
                          description: 'Fair market value of the card in USD. Null when unknown.',
                        },
                        badge: {
                          type: 'object',
                          nullable: true,
                          title: 'CardBadge',
                          description: 'Tier badge for the card. Null when none.',
                          required: ['id', 'name'],
                          properties: {
                            id: { type: 'integer', description: 'Badge identifier.' },
                            name: { type: 'string', description: 'Badge label (tier).' },
                          },
                        },
                        best_active_offer: {
                          type: 'object',
                          nullable: true,
                          title: 'BuybackOffer',
                          description: 'The current buyback offer for selling this card back. Null when none is open.',
                          required: ['id', 'gross_price', 'net_price', 'created_at'],
                          properties: {
                            id: { type: 'integer', description: 'Buyback offer identifier.' },
                            gross_price: {
                              type: 'number',
                              format: 'float',
                              description: 'Amount the player receives in USD before fees.',
                            },
                            net_price: {
                              type: 'number',
                              format: 'float',
                              description: 'Amount the player receives in USD after the marketplace fee.',
                            },
                            buyback_rate: {
                              type: 'number',
                              format: 'float',
                              nullable: true,
                              description: 'Buyback rate applied to this offer. Null when not set.',
                            },
                            auto_sell_at: {
                              type: 'string',
                              format: 'date-time',
                              nullable: true,
                              description: 'When the card would be sold back automatically. Null when not scheduled.',
                            },
                            created_at: {
                              type: 'string',
                              format: 'date-time',
                              description: 'When the buyback offer was created.',
                            },
                          },
                        },
                        status: {
                          type: 'string',
                          nullable: true,
                          description:
                            'State of the card (e.g. "listed", "on_hands", "redeemed", "buyback", "burned", "reserved", "unavailable"). May be null.',
                        },
                        pool_order_id: {
                          type: 'integer',
                          nullable: true,
                          description: 'Identifier of the purchase round this card belongs to. Null when not tied to a round.',
                        },
                        sold_from_pool_offer: {
                          type: 'object',
                          nullable: true,
                          title: 'CardSourceGame',
                          description: 'The game this card came from, for cross-selling. Null when unknown.',
                          required: ['id', 'title'],
                          properties: {
                            id: { type: 'integer', description: 'Source game identifier.' },
                            title: { type: 'string', description: 'Source game name — reflects the pack rename set in the console, when one is configured.' },
                            image_url: {
                              type: 'string',
                              nullable: true,
                              format: 'uri',
                              description: 'Source game pack image. Null when no image is set.',
                            },
                          },
                        },
                        card_details: { $ref: '#/components/schemas/CardDetails' },
                      },
                    },
                  },
                  meta: { $ref: '#/components/schemas/PaginationMeta' },
                },
              },
              example: {
                pool_items: [
                  {
                    id: 778231,
                    title: 'Conor McGregor 2021 Prizm Auto /25',
                    image_url: 'https://cdn.getcardbase.com/blobs/item_778231.png',
                    item_price: 1450.0,
                    opened_at: null,
                    fair_market_value: 1450.0,
                    badge: { id: 7, name: 'Legendary' },
                    best_active_offer: {
                      id: 33120,
                      gross_price: 1305.0,
                      net_price: 1226.7,
                      buyback_rate: 0.1,
                      auto_sell_at: null,
                      created_at: '2026-05-28T14:03:11.000Z',
                    },
                    status: 'listed',
                    pool_order_id: null,
                    sold_from_pool_offer: {
                      id: 463,
                      title: 'HOF ELITE MMA Pack',
                      image_url: 'https://cdn.getcardbase.com/blobs/pack_463.png',
                    },
                  },
                ],
                meta: { total_count: 50, page: 1, per: 32 },
              },
            },
          },
        },
        '422': {
          description: 'The cards for this game could not be retrieved.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: 'No game with this id is available to the player.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '401': {
          description: 'The player session token is missing, invalid, or expired.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected server error. The response carries a tracking id you can quote to support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_offers/{pool_offer_id}/custom_pack/items': {
    get: {
      tags: ['Catalog'],
      summary: 'Search cards for a custom pack',
      description: [
        'Search the cards a player can put into a custom pack — the first step of building one. Call it for a game',
        'whose `game_kind` is `custom_pack` (see **Get a game**). When the pack has its own card list, it offers only',
        'those cards; otherwise every card of your inventory that can go into a pack.',
        '',
        'Cards come back ordered by fair market value (`sort`, highest first by default) and narrowed by the',
        'optional filters. `facets` lists the options to offer next to the grid: send an option’s `id` back in the',
        'matching filter (`category_id`, `sport[]`, `grader[]`, `grade[]`, `year[]`, `set[]`, `pokemon_rarity[]`).',
        '`price` does the same for the price filter: `ranges` lists the price ranges to offer, to send back as',
        '`fmv_ranges[]`. It ignores the price filter itself, so every range stays on offer while one is applied.',
        '',
        'Only page 1 carries `price` and `facets`: a later page (`page` 2 and up) holds `items` and `meta` and',
        'nothing else. `facets` is also left out of page 1 when the request sends `facets=0` — send it when the',
        'player narrows the search and you already hold the options. A missing `price` or `facets`, on any page,',
        'means "keep what you hold": it is not an empty list.',
        '',
        'The response carries no totals or counts. Page with `page` / `per` and keep going while `meta.has_more` is',
        '`true`; a page can hold fewer than `per` cards. Paging stops at the search window: `has_more` is `false` once',
        '`(page + 1) × per` would exceed 10,000 results, and a page beyond the window answers `200` with an empty',
        'page — narrow the filters to reach further.',
        '',
        'A card listed here can be won by another player before the pick is bought: **Get a custom pack quote** and',
        '**Buy a pack (one call)** check every card again (`CARD_UNAVAILABLE`).',
        '',
        'Searching is rate-limited to 60 requests per 60 seconds for each player session. Visitors who have not',
        'signed in use the anonymous twin, **Search cards for a custom pack (anonymous)**.',
      ].join('\n'),
      operationId: 'searchCustomPackCards',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Identifier of the custom pack game — one whose `game_kind` is `custom_pack`.',
        },
        ...customPackSearchParameters,
      ],
      responses: {
        '200': {
          description:
            'A page of cards. Page 1 also carries the filter options (`facets`, unless the request sent `facets=0`) and ' +
            'the price ranges (`price`) to offer; a later page carries `items` and `meta` only. The body is not ' +
            'wrapped in `result`.',
          content: { 'application/json': { schema: customPackCardsSchema, examples: customPackCardsExamples } },
        },
        '401': {
          description: 'The player session token is missing, invalid, or expired.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description:
            '`NOT_FOUND` — no available custom pack has this id: the game does not exist or is a regular pack, or ' +
            `the custom pack is unavailable because ${CUSTOM_PACK_UNAVAILABLE_REASONS}.`,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '429': {
          description:
            '`RATE_LIMITED` — more than 60 requests in 60 seconds for this player session. `CATALOG_BUSY` — the ' +
            'search did not answer in four seconds because it is busy. Wait `Retry-After` seconds before ' +
            'searching again, and not in a tight loop; the request changed nothing.',
          headers: retryAfterHeaders,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '503': {
          description:
            '`SEARCH_UNAVAILABLE` — the card search is temporarily down. Retry shortly, and not in a tight loop; ' +
            'the request changed nothing.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_offers/{pool_offer_id}/custom_pack/quote': {
    post: {
      tags: ['Catalog'],
      summary: 'Get a custom pack quote',
      description: [
        'Price a selection without moving money: send the picked cards with their chances and get back what the',
        'pack costs right now. Call it whenever the selection changes, and once more just before buying.',
        '',
        'The price is the expected value of the selection divided by the game’s RTP, rounded up to the cent. All',
        'figures are whole numbers, so you can reproduce them exactly: with `N` the sum of `chance_permille ×',
        'fair_market_value_cents` over the items, `price_cents = ceil(N × 1000 ÷ rtp_ppm)` and',
        '`ev_cents = floor((N + 500) ÷ 1000)`.',
        '',
        'A quote holds neither the price nor the cards: card values move, and another player can win a card at any',
        'time. **Buy a pack (one call)** prices the selection again and refuses the purchase (`409`) when the price',
        'is no longer the one you confirmed.',
        '',
        'A selection that breaks a rule, or holds a card that can no longer be picked, answers `422`',
        '`CUSTOM_PACK_INVALID_SELECTION`; `error_detail.details` lists the problems found.',
        '',
        'Quoting is rate-limited to 60 requests per 60 seconds for each player session. A player session is',
        'required — an anonymous visitor signs in first.',
      ].join('\n'),
      operationId: 'quoteCustomPack',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Identifier of the custom pack game — one whose `game_kind` is `custom_pack`.',
        },
      ],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'CustomPackQuoteRequest',
              required: ['custom_pack'],
              properties: {
                custom_pack: {
                  type: 'object',
                  required: ['items'],
                  properties: { items: customPackSelectionItems },
                },
              },
            },
            example: { custom_pack: { items: customPackSelectionItems.example } },
          },
        },
      },
      responses: {
        '200': {
          description: 'The selection priced right now. Nothing is charged or held.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'CustomPackQuoteResponse',
                required: ['quote'],
                properties: { quote: customPackQuoteSchema },
              },
              example: { quote: customPackQuoteExample },
            },
          },
        },
        '401': {
          description: 'The player session token is missing, invalid, or expired.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description:
            '`NOT_FOUND` — no available custom pack has this id: the game does not exist or is a regular pack, or ' +
            `the custom pack is unavailable because ${CUSTOM_PACK_UNAVAILABLE_REASONS}. ${CUSTOM_PACK_UNPRICED_NOTE}`,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description:
            '`CUSTOM_PACK_INVALID_SELECTION` — the selection cannot be priced. `error_detail.details` lists the ' +
            'problems found, each with a `code` (`SELECTION_MALFORMED`, `TOO_FEW_CARDS`, `TOO_MANY_CARDS`, ' +
            '`DUPLICATE_CARD`, `CHANCE_OUT_OF_RANGE`, `SUM_NOT_1000` or `CARD_UNAVAILABLE`) and, for a problem with ' +
            'one card, its `pool_item_id`. Fix the selection and quote again.',
          content: {
            'application/json': { schema: customPackErrorSchema, example: customPackInvalidSelectionExample },
          },
        },
        '429': {
          description:
            '`RATE_LIMITED` — more than 60 requests in 60 seconds for this player session. Wait `Retry-After` ' +
            'seconds before quoting again.',
          headers: retryAfterHeaders,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },
};
